import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Plus, Search, Filter, GitBranch, Edit2, Trash2, Play, Clock, X, AlertTriangle, Loader2, ChevronUp, ChevronDown, RefreshCw, ArrowLeft, Copy } from 'lucide-react';
import { useProjectStore } from '../../store';
import { useAllWorkflows } from '../../hooks/queries/useAllWorkflows';
import {
  useCreateWorkflow,
  useUpdateWorkflow,
  useDeleteWorkflow,
} from '../../hooks/queries/useWorkflowMutations';
import { Workflow, CreateWorkflowForm, Schedule, Queue } from '../../types';
import WorkflowFormModal from './WorkflowFormModal';
import CopyWorkflowModal from './CopyWorkflowModal';
import apiService from '../../services/api';
import ScheduleIndicator from '../Schedule/ScheduleIndicator';
import { QueueIndicator } from '../Queue';
import CustomSelect from '../common/CustomSelect';
import Tooltip from '../common/Tooltip';

interface WorkflowFilters {
  search: string;
  status: 'all' | 'pending' | 'running' | 'completed' | 'failed';
}

interface SortConfig {
  field: 'name' | 'description' | 'created_at' | 'updated_at' | 'status' | 'next_run';
  direction: 'asc' | 'desc';
}

const WorkflowsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  // AC-1412 / AC-1377 P5.1: workflow CRUD flows through dedicated React
  // Query mutation hooks. AC-1410 already migrated `allWorkflows` to
  // `useAllWorkflows`; this ticket finishes the cleanup by routing
  // create/update/delete through the mutation hooks too. Each mutation
  // invalidates `queryKeys.workflows()` (and the workflow detail key for
  // update/delete), so the list refreshes automatically without the
  // explicit invalidateAllWorkflows() calls the migration shim used to
  // need.
  const { currentProject } = useProjectStore();
  const allWorkflowsQuery = useAllWorkflows();
  const allWorkflows = allWorkflowsQuery.data ?? [];
  const createWorkflowMutation = useCreateWorkflow();
  const updateWorkflowMutation = useUpdateWorkflow();
  const deleteWorkflowMutation = useDeleteWorkflow();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editWorkflow, setEditWorkflow] = useState<Workflow | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; workflow: Workflow | null }>({ open: false, workflow: null });
  // AC-1598: copy-target workflow (drives the CopyWorkflowModal). Null when
  // the modal is closed; set to the row's workflow when the Duplicate action
  // is clicked.
  const [copyTargetWorkflow, setCopyTargetWorkflow] = useState<Workflow | null>(null);

  // Schedules map: orchestration_id -> Schedule
  const [schedulesMap, setSchedulesMap] = useState<Map<number, Schedule>>(new Map());

  // Queues map: orchestration_id -> Queue[] (queues this workflow reads from)
  const [queuesMap, setQueuesMap] = useState<Map<number, Queue[]>>(new Map());

  // Filters and sorting
  const [filters, setFilters] = useState<WorkflowFilters>({
    search: '',
    status: 'all'
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'name',
    direction: 'asc'
  });
  const [showFilters, setShowFilters] = useState(false);

  // Load schedules and create a map by orchestration_id
  const loadSchedules = useCallback(async () => {
    try {
      const schedules = await apiService.listSchedules();
      const map = new Map<number, Schedule>();
      for (const schedule of schedules) {
        map.set(schedule.orchestration_id, schedule);
      }
      setSchedulesMap(map);
    } catch (err) {
      console.error('Error loading schedules:', err);
      // Don't show error to user - schedules are optional enhancement
    }
  }, []);

  // Helper to get schedule for a workflow
  const getScheduleForWorkflow = useCallback((workflow: Workflow): Schedule | null => {
    const orchId = typeof workflow.orchestration_id === 'string'
      ? parseInt(workflow.orchestration_id, 10)
      : workflow.orchestration_id;
    return schedulesMap.get(orchId) || null;
  }, [schedulesMap]);

  // Load queues and build a map of orchestration_id -> Queue[]
  const loadQueues = useCallback(async () => {
    if (!projectId) return;
    try {
      // Get all queues for the project
      const queues = await apiService.getQueues(projectId);

      // For each queue, get consumer configs to find which workflows read from it
      const consumerConfigPromises = queues.map(async (queue) => {
        try {
          const configs = await apiService.getQueueConsumerConfigs(queue.queue_id);
          return { queue, configs };
        } catch {
          return { queue, configs: [] };
        }
      });

      const results = await Promise.all(consumerConfigPromises);

      // Build reverse map: orchestration_id -> Queue[]
      const map = new Map<number, Queue[]>();
      for (const { queue, configs } of results) {
        for (const config of configs) {
          const existing = map.get(config.orchestration_id) || [];
          // Avoid duplicates
          if (!existing.some(q => q.queue_id === queue.queue_id)) {
            existing.push(queue);
          }
          map.set(config.orchestration_id, existing);
        }
      }
      setQueuesMap(map);
    } catch (err) {
      console.error('Error loading queues:', err);
      // Don't show error to user - queues are optional enhancement
    }
  }, [projectId]);

  // Helper to get queues for a workflow
  const getQueuesForWorkflow = useCallback((workflow: Workflow): Queue[] => {
    const orchId = typeof workflow.orchestration_id === 'string'
      ? parseInt(workflow.orchestration_id, 10)
      : workflow.orchestration_id;
    return queuesMap.get(orchId) || [];
  }, [queuesMap]);

  useEffect(() => {
    if (currentProject) {
      // useAllWorkflows() above subscribes on mount - no manual fetch
      // needed. We still drive the schedules / queues side-fetches here.
      loadSchedules();
      loadQueues();
    }
  }, [currentProject, loadSchedules, loadQueues]);

  const handleOpenModal = () => {
    setEditWorkflow(null);
    setShowCreateModal(true);
    // Tutorial step 9 is advanced by TutorialOverlay via click trigger on btn-create-workflow
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setEditWorkflow(null);
  };

  const handleCreate = async (data: CreateWorkflowForm) => {
    if (!currentProject) return;
    setLoading(true);
    setError(null);
    try {
      if (editWorkflow) {
        // AC-1412 / AC-1377 P5.1: useUpdateWorkflow's onSuccess
        // invalidates `queryKeys.workflow(id)` AND `queryKeys.workflows()`
        // — no manual invalidateAllWorkflows() call needed.
        await updateWorkflowMutation.mutateAsync({
          workflowId: editWorkflow.orchestration_id,
          data,
        });
        setShowCreateModal(false);
        setEditWorkflow(null);
      } else {
        const workflow = await createWorkflowMutation.mutateAsync({
          projectId: currentProject.project_id,
          data,
        });
        setShowCreateModal(false);
        // Tutorial step 10 is advanced by TutorialOverlay via click trigger on btn-submit-workflow
        navigate(`/projects/${currentProject.project_id}/workflows/${workflow.orchestration_id}`);
      }
    } catch (err) {
      setError(editWorkflow ? 'Failed to update workflow' : 'Failed to create workflow');
      console.error('Error saving workflow:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (workflow: Workflow) => {
    setEditWorkflow(workflow);
    setShowCreateModal(true);
  };

  const handleView = (workflow: Workflow) => {
    navigate(`/projects/${projectId}/workflows/${workflow.orchestration_id}`);
  };

  const handleDelete = async () => {
    if (deleteDialog.workflow) {
      setLoading(true);
      setError(null);
      try {
        // AC-1412 / AC-1377 P5.1: useDeleteWorkflow's onSuccess removes
        // the workflow detail entry from cache and invalidates the
        // workflows list automatically.
        await deleteWorkflowMutation.mutateAsync({
          workflowId: deleteDialog.workflow.orchestration_id,
        });
        setDeleteDialog({ open: false, workflow: null });
      } catch (err: any) {
        console.error('Error deleting workflow:', err);
        if (err?.response?.data?.detail) {
          setError(err.response.data.detail);
        } else if (err?.message) {
          setError(err.message);
        } else {
          setError('Failed to delete workflow. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSort = (field: SortConfig['field']) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter and sort workflows
  const filteredAndSortedWorkflows = useMemo(() => {
    let filtered = allWorkflows.filter(orch => String(orch.project_id) === projectId);
    
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(orch => 
        orch.name.toLowerCase().includes(searchLower) ||
        orch.description?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(orch => orch.status === filters.status);
    }

    // Sort
    // AC-1420: next_run sorts by the schedule's next_run_at (workflows with
    // no schedule sort to the bottom regardless of direction so they don't
    // crowd out the actionable, scheduled rows).
    const nextRunMs = (workflow: Workflow): number | null => {
      const sched = getScheduleForWorkflow(workflow);
      if (!sched?.next_run_at) return null;
      const t = new Date(sched.next_run_at).getTime();
      return Number.isNaN(t) ? null : t;
    };

    filtered.sort((a, b) => {
      if (sortConfig.field === 'next_run') {
        const aMs = nextRunMs(a);
        const bMs = nextRunMs(b);
        if (aMs === null && bMs === null) return 0;
        if (aMs === null) return 1;
        if (bMs === null) return -1;
        return sortConfig.direction === 'asc' ? aMs - bMs : bMs - aMs;
      }

      let aValue: any = (a as any)[sortConfig.field];
      let bValue: any = (b as any)[sortConfig.field];

      if (sortConfig.field === 'created_at' || sortConfig.field === 'updated_at') {
        aValue = new Date(aValue || '').getTime();
        bValue = new Date(bValue || '').getTime();
      } else if (sortConfig.field === 'description') {
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || '';
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [allWorkflows, projectId, filters, sortConfig, getScheduleForWorkflow]);

  // AC-1420: render a short summary like "every 15 min" / "0 9 * * *" /
  // "one-time" so the table conveys the schedule cadence without forcing the
  // user to hover the small clock icon.
  const formatScheduleSummary = (schedule: Schedule | null): string => {
    if (!schedule) return '';
    if (!schedule.is_enabled) return 'paused';
    if (schedule.schedule_type === 'interval' && schedule.interval_minutes != null) {
      const m = schedule.interval_minutes;
      if (m < 60) return `every ${m} min`;
      if (m % 60 === 0) return `every ${m / 60} hr`;
      return `every ${m} min`;
    }
    if (schedule.schedule_type === 'cron' && schedule.cron_expression) {
      return schedule.cron_expression;
    }
    if (schedule.schedule_type === 'one_time') return 'one-time';
    return '';
  };

  const getSortIcon = (field: SortConfig['field']) => {
    if (sortConfig.field !== field) {
      return <div className="w-4 h-4" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="w-4 h-4" /> : 
      <ChevronDown className="w-4 h-4" />;
  };

  const getStatusBadge = (status?: string) => {
    const statusColors = {
      pending: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
      running: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    
    const color = statusColors[status as keyof typeof statusColors] || statusColors.pending;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {status || 'pending'}
      </span>
    );
  };

  if (!currentProject) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <GitBranch className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-2">
            No project selected
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Please select a project to manage workflows
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Back to Overview */}
      <div className="mb-4">
        <Link
          to={`/projects/${projectId}`}
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Overview
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">Workflows</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Create and manage your workflows for {currentProject.name}
          </p>
        </div>
        <button
          onClick={handleOpenModal}
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
          disabled={loading}
          data-tutorial="btn-create-workflow"
        >
          <Plus className="w-4 h-4" />
          New Workflow
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="text-red-700 dark:text-red-300 whitespace-pre-line">{error}</div>
            </div>
            <button 
              onClick={() => setError(null)}
              className="ml-2 text-red-500 hover:text-red-700 flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search workflows..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border ${
              showFilters
                ? 'bg-primary-50 dark:bg-primary-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                <CustomSelect
                  value={filters.status}
                  onChange={(value) => setFilters(prev => ({
                    ...prev,
                    status: value as WorkflowFilters['status']
                  }))}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'running', label: 'Running' },
                    { value: 'completed', label: 'Completed' },
                    { value: 'failed', label: 'Failed' },
                  ]}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading workflows...</span>
        </div>
      )}

      {/* Workflows List */}
      {!loading && (
        <>
          {filteredAndSortedWorkflows.length === 0 ? (
            <div className="text-center py-12">
              <GitBranch className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-2">
                {allWorkflows.filter(o => String(o.project_id) === projectId).length === 0 ? 'No workflows yet' : 'No workflows match your filters'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {allWorkflows.filter(o => String(o.project_id) === projectId).length === 0 
                  ? 'Get started by creating your first workflow'
                  : 'Try adjusting your search or filters'}
              </p>
              {allWorkflows.filter(o => String(o.project_id) === projectId).length === 0 && (
                <button
                  onClick={handleOpenModal}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <Plus className="w-4 h-4" />
                  Create Workflow
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop View */}
              <div className="hidden lg:block bg-white dark:bg-cap-dark shadow rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 table-auto">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                    <th
                      scope="col"
                      className="min-w-[200px] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        Name
                        {getSortIcon('name')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="min-w-[250px] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('description')}
                    >
                      <div className="flex items-center gap-1">
                        Description
                        {getSortIcon('description')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="min-w-[100px] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="min-w-[120px] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Created
                        {getSortIcon('created_at')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="min-w-[120px] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('updated_at')}
                    >
                      <div className="flex items-center gap-1">
                        Updated
                        {getSortIcon('updated_at')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="min-w-[160px] px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('next_run')}
                    >
                      <div className="flex items-center gap-1">
                        Next Run
                        {getSortIcon('next_run')}
                      </div>
                    </th>
                    <th scope="col" className="min-w-[120px] relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredAndSortedWorkflows.map((workflow) => (
                      <tr
                        key={workflow.orchestration_id}
                        onClick={() => handleView(workflow)}
                        className="hover:bg-primary-50 dark:hover:bg-primary-900/20 dark:bg-gray-900 transition-colors cursor-pointer"
                      >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                              <GitBranch className="w-5 h-5 text-orange-600" />
                            </div>
                          </div>
                          <div className="ml-4 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-warm-white break-words">
                                {workflow.name}
                              </span>
                              <ScheduleIndicator schedule={getScheduleForWorkflow(workflow)} size="sm" />
                              <QueueIndicator queues={getQueuesForWorkflow(workflow)} projectId={projectId!} size="sm" />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 dark:text-gray-400 break-words pr-4">
                          {workflow.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(workflow.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {workflow.created_at ? new Date(workflow.created_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {workflow.updated_at ? new Date(workflow.updated_at).toLocaleDateString() : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const sched = getScheduleForWorkflow(workflow);
                          if (!sched) {
                            return <span className="text-sm text-gray-400 dark:text-gray-500">—</span>;
                          }
                          const summary = formatScheduleSummary(sched);
                          return (
                            <div className="text-sm">
                              <div className="text-gray-700 dark:text-gray-300">
                                {sched.next_run_at
                                  ? new Date(sched.next_run_at).toLocaleString()
                                  : <span className="text-gray-400 dark:text-gray-500">—</span>}
                              </div>
                              {summary && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">{summary}</div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          <Tooltip content="Edit workflow">
                            <button
                              onClick={() => handleEdit(workflow)}
                              className="text-primary-600 hover:text-primary-800"
                              aria-label="Edit workflow"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Duplicate workflow">
                            <button
                              onClick={() => setCopyTargetWorkflow(workflow)}
                              className="text-primary-600 hover:text-primary-800"
                              aria-label="Duplicate workflow"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content="Delete workflow">
                            <button
                              onClick={() => setDeleteDialog({ open: true, workflow })}
                              className="text-red-600 hover:text-red-800"
                              aria-label="Delete workflow"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile/Tablet View - Card Layout */}
              <div className="lg:hidden space-y-4">
                {filteredAndSortedWorkflows.map((workflow) => (
                  <div
                    key={workflow.orchestration_id}
                    onClick={() => handleView(workflow)}
                    className="bg-white dark:bg-cap-dark shadow rounded-lg p-4 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:shadow-lg border border-transparent hover:border-primary-400 transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                            <GitBranch className="w-5 h-5 text-orange-600" />
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-warm-white break-words">
                              {workflow.name}
                            </h3>
                            <ScheduleIndicator schedule={getScheduleForWorkflow(workflow)} size="sm" />
                            <QueueIndicator queues={getQueuesForWorkflow(workflow)} projectId={projectId!} size="sm" />
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 break-words mt-1">
                            {workflow.description || 'No description'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2" onClick={(e) => e.stopPropagation()}>
                        <Tooltip content="Edit workflow">
                          <button
                            onClick={() => handleEdit(workflow)}
                            className="p-1.5 text-primary-600 hover:text-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded"
                            aria-label="Edit workflow"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Duplicate workflow">
                          <button
                            onClick={() => setCopyTargetWorkflow(workflow)}
                            className="p-1.5 text-primary-600 hover:text-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded"
                            aria-label="Duplicate workflow"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Delete workflow">
                          <button
                            onClick={() => setDeleteDialog({ open: true, workflow })}
                            className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                            aria-label="Delete workflow"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        {getStatusBadge(workflow.status)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>Created: {workflow.created_at ? new Date(workflow.created_at).toLocaleDateString() : 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <RefreshCw className="w-3 h-3" />
                        <span>Updated: {workflow.updated_at ? new Date(workflow.updated_at).toLocaleDateString() : 'N/A'}</span>
                      </div>
                      {(() => {
                        const sched = getScheduleForWorkflow(workflow);
                        if (!sched?.next_run_at) return null;
                        const summary = formatScheduleSummary(sched);
                        return (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>
                              Next: {new Date(sched.next_run_at).toLocaleString()}
                              {summary && <span className="ml-1 text-gray-400 dark:text-gray-500">({summary})</span>}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <WorkflowFormModal
          isOpen={showCreateModal}
          onClose={handleCloseModal}
          onSubmit={handleCreate}
          workflow={editWorkflow}
        />
      )}

      {/* AC-1598: Copy / Duplicate Modal */}
      {copyTargetWorkflow && projectId && (
        <CopyWorkflowModal
          isOpen={true}
          onClose={() => setCopyTargetWorkflow(null)}
          sourceWorkflow={{
            orchestration_id:
              typeof copyTargetWorkflow.orchestration_id === 'string'
                ? parseInt(copyTargetWorkflow.orchestration_id, 10)
                : copyTargetWorkflow.orchestration_id,
            name: copyTargetWorkflow.name,
            project_id: parseInt(String(copyTargetWorkflow.project_id), 10),
          }}
          currentProjectId={parseInt(projectId, 10)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialog.open && deleteDialog.workflow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-4">Delete Workflow</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the workflow "{deleteDialog.workflow.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteDialog({ open: false, workflow: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowsPage; 